/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Save, Tag, Hash, Box, FileText, User, Activity, Layers, ListTree
} from 'lucide-react';
import { serviceRequestTypeService } from '../services/serviceRequestTypeService';
import StatusBadge from '../components/StatusBadge';
import toastService from '../utils/toastService';
import { useAuth } from '../context/AuthContext';
import { SERVICE_REQUEST_LABEL } from '../constants/entityLabels';
import { DEFAULT_REQUEST_NUMBER_LABEL } from '../utils/serviceRequestLabels';
import HierarchyLevelManager from '../components/hierarchy/HierarchyLevelManager';
import HierarchyNodeAdder from '../components/hierarchy/HierarchyNodeAdder';
import HierarchyTreeView from '../components/hierarchy/HierarchyTreeView';

const TYPES_ROUTE = '/service-requests/types';

const EditServiceRequestType = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [activeTab, setActiveTab] = useState('general'); // 'general' | 'hierarchy'

  // Hierarchy state
  const [hierarchyLevels, setHierarchyLevels] = useState([]);
  const [hierarchyNodes, setHierarchyNodes] = useState([]);
  const [hierarchyTree, setHierarchyTree] = useState([]);
  const [hierarchyLoading, setHierarchyLoading] = useState(false);
  const [includeInactiveHierarchy, setIncludeInactiveHierarchy] = useState(false);

  // Quick-add state for adder pre-fill
  const [quickAddTargetDepth, setQuickAddTargetDepth] = useState(null);
  const [quickAddParentUid, setQuickAddParentUid] = useState(null);

  const [form, setForm] = useState({
    name: '',
    requestNumberLabel: '',
    customerNameLabel: '',
    assetIdentifierLabel: '',
    equipmentIdentifierLabel: '',
    isActive: true,
  });

  const loadHierarchy = async (overrideIncludeInactive) => {
    try {
      setHierarchyLoading(true);
      const incInactive =
        overrideIncludeInactive !== undefined ? overrideIncludeInactive : includeInactiveHierarchy;
      const { data } = await serviceRequestTypeService.getHierarchy(id, {
        includeInactive: incInactive,
      });
      setHierarchyLevels(data.levels || []);
      setHierarchyNodes(data.nodes || []);
      setHierarchyTree(data.tree || []);
    } catch {
      // Not critical if fails on initial load
    } finally {
      setHierarchyLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadHierarchy();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, includeInactiveHierarchy]);


  useEffect(() => {
    if (user && user.scope !== 'platform') {
      toastService.error(`Only platform users can edit ${SERVICE_REQUEST_LABEL.singularLower} types`);
      navigate(TYPES_ROUTE, { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const { data } = await serviceRequestTypeService.getServiceRequestTypeById(id);
        setForm({
          name: data.name || '',
          requestNumberLabel: data.requestNumberLabel || '',
          customerNameLabel: data.customerNameLabel || '',
          assetIdentifierLabel: data.assetIdentifierLabel || '',
          equipmentIdentifierLabel: data.equipmentIdentifierLabel || '',
          isActive: !!data.isActive,
        });
      } catch {
        toastService.error(`Failed to load ${SERVICE_REQUEST_LABEL.singularLower} type`);
        navigate(TYPES_ROUTE);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id, navigate]);

  const validateForm = () => {
    const errors = {};
    if (!form.name.trim()) { errors.name = 'Name is required'; }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleBlur = (field, value) => {
    if (field === 'name' && !value.trim()) {
      setFieldErrors(prev => ({ ...prev, name: 'Name is required' }));
    } else {
      setFieldErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) { return; }
    try {
      setSubmitting(true);
      const payload = {
        name: form.name.trim(),
        requestNumberLabel: form.requestNumberLabel.trim() || undefined,
        customerNameLabel: form.customerNameLabel.trim() || undefined,
        assetIdentifierLabel: form.assetIdentifierLabel.trim() || undefined,
        equipmentIdentifierLabel: form.equipmentIdentifierLabel.trim() || undefined,
        isActive: form.isActive,
      };
      await serviceRequestTypeService.updateServiceRequestType(id, payload);
      toastService.success(`${SERVICE_REQUEST_LABEL.singular} type updated successfully`);
      navigate(TYPES_ROUTE);
    } catch (err) {
      const msg = err.response?.data?.message;
      toastService.error(Array.isArray(msg) ? msg[0] : (msg || `Failed to update ${SERVICE_REQUEST_LABEL.singularLower} type`));
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = (field) =>
    `w-full px-4 py-2.5 bg-base-100 border rounded-xl text-sm text-base-content placeholder:text-base-content/30 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all ${fieldErrors[field] ? 'border-error' : 'border-base-300'}`;

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse p-2 w-full">
        <div className="h-6 w-48 bg-base-300 rounded-lg"></div>
        <div className="glass-card rounded-2xl p-6 h-36 bg-base-100/60"></div>
        <div className="glass-card rounded-2xl p-6 h-48 bg-base-100/60"></div>
        <div className="glass-card rounded-2xl p-6 h-48 bg-base-100/60"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-1 sm:p-2 w-full pb-12">
      {/* Top Breadcrumb */}
      <div>
        <button
          type="button"
          onClick={() => navigate(TYPES_ROUTE)}
          className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-base-content/50 hover:text-primary transition-colors group"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
          Back to {SERVICE_REQUEST_LABEL.singular} Types
        </button>
      </div>

      {/* Hero Header Banner */}
      <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1.5 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-black uppercase tracking-widest text-base-content/40">
                Type Configuration
              </span>
              <StatusBadge status={form.isActive ? 'active' : 'inactive'} />
            </div>
            <h1 className="text-2xl sm:text-3xl font-black text-base-content tracking-tight">
              {form.name || `Update ${SERVICE_REQUEST_LABEL.singular} Type`}
            </h1>
            <p className="text-xs text-base-content/50">
              Modify category properties, active availability, and custom identifier display labels.
            </p>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="flex items-center gap-2 border-b border-base-content/10 pb-1">
        <button
          type="button"
          onClick={() => setActiveTab('general')}
          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all ${
            activeTab === 'general'
              ? 'bg-primary text-primary-content shadow-sm'
              : 'text-base-content/60 hover:text-base-content hover:bg-base-200'
          }`}
        >
          <Tag size={15} />
          <span>General Settings</span>
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('hierarchy')}
          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all ${
            activeTab === 'hierarchy'
              ? 'bg-primary text-primary-content shadow-sm'
              : 'text-base-content/60 hover:text-base-content hover:bg-base-200'
          }`}
        >
          <ListTree size={15} />
          <span>Hierarchy Tree</span>
          {hierarchyLevels.length > 0 && (
            <span
              className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                activeTab === 'hierarchy'
                  ? 'bg-primary-content/20 text-primary-content'
                  : 'bg-base-300 text-base-content'
              }`}
            >
              {hierarchyLevels.length} Levels • {hierarchyNodes.length} Options
            </span>
          )}
        </button>
      </div>

      {activeTab === 'general' && (
        <form onSubmit={handleSubmit} className="space-y-6">
        {/* Category Information & Status */}
        <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
          <div className="flex items-center gap-2 pb-3 border-b border-base-content/10">
            <Tag size={18} className="text-primary" />
            <h2 className="text-base font-black text-base-content uppercase tracking-wider">
              Category & Status
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Name */}
            <div className="space-y-1.5">
              <label htmlFor="name" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5">
                <Tag size={12} className="text-primary" />
                <span>Type Name *</span>
              </label>
              <input
                id="name"
                type="text"
                maxLength={100}
                placeholder="e.g. Automotive"
                className={inputClass('name')}
                value={form.name}
                onBlur={e => handleBlur('name', e.target.value)}
                onChange={e => { setForm(f => ({ ...f, name: e.target.value })); setFieldErrors(p => ({ ...p, name: null })); }}
              />
              {fieldErrors.name && <p className="text-error text-xs font-medium">{fieldErrors.name}</p>}
            </div>

            {/* Status */}
            <div className="space-y-1.5">
              <label htmlFor="isActive" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5">
                <Activity size={12} className="text-primary" />
                <span>Status</span>
              </label>
              <select
                id="isActive"
                className="w-full px-4 py-2.5 bg-base-100 border border-base-300 rounded-xl text-sm text-base-content focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-medium"
                value={form.isActive ? 'active' : 'inactive'}
                onChange={e => setForm(f => ({ ...f, isActive: e.target.value === 'active' }))}
              >
                <option value="active">Active (Selectable in new {SERVICE_REQUEST_LABEL.pluralLower})</option>
                <option value="inactive">Inactive (Hidden when creating {SERVICE_REQUEST_LABEL.pluralLower})</option>
              </select>
            </div>
          </div>
        </div>

        {/* Custom Field Labels & Identifiers */}
        <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-base-content/10">
            <div className="flex items-center gap-2">
              <Hash size={18} className="text-primary" />
              <h2 className="text-base font-black text-base-content uppercase tracking-wider">
                Custom Field Labels
              </h2>
            </div>
            <span className="text-[10px] text-base-content/40 font-mono">Optional Customization</span>
          </div>

          <p className="text-xs text-base-content/60">
            Override default field labels for {SERVICE_REQUEST_LABEL.pluralLower} created under this category. Leave blank to use system defaults.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {/* Request Number Label */}
            <div className="space-y-1.5">
              <label htmlFor="requestNumberLabel" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5">
                <FileText size={12} className="text-primary" />
                <span>{SERVICE_REQUEST_LABEL.singular} Number Label</span>
              </label>
              <input
                id="requestNumberLabel"
                type="text"
                maxLength={100}
                placeholder="e.g. Repair Order Number"
                className={inputClass('requestNumberLabel')}
                value={form.requestNumberLabel}
                onChange={e => setForm(f => ({ ...f, requestNumberLabel: e.target.value }))}
              />
              <p className="text-base-content/40 text-[10px] font-medium">
                Default: "{DEFAULT_REQUEST_NUMBER_LABEL}"
              </p>
            </div>

            {/* Customer Name Label */}
            <div className="space-y-1.5">
              <label htmlFor="customerNameLabel" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5">
                <User size={12} className="text-primary" />
                <span>Customer Name Label</span>
              </label>
              <input
                id="customerNameLabel"
                type="text"
                maxLength={100}
                placeholder="e.g. Guest Name, Owner Name"
                className={inputClass('customerNameLabel')}
                value={form.customerNameLabel}
                onChange={e => setForm(f => ({ ...f, customerNameLabel: e.target.value }))}
              />
              <p className="text-base-content/40 text-[10px] font-medium">
                Default: "Customer Name"
              </p>
            </div>

            {/* Asset Identifier Label */}
            <div className="space-y-1.5">
              <label htmlFor="assetIdentifierLabel" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5">
                <Hash size={12} className="text-primary" />
                <span>Asset Identifier Label</span>
              </label>
              <input
                id="assetIdentifierLabel"
                type="text"
                maxLength={100}
                placeholder="e.g. License Plate, Unit #"
                className={inputClass('assetIdentifierLabel')}
                value={form.assetIdentifierLabel}
                onChange={e => setForm(f => ({ ...f, assetIdentifierLabel: e.target.value }))}
              />
              <p className="text-base-content/40 text-[10px] font-medium">
                Default: "Asset Identifier"
              </p>
            </div>

            {/* Equipment Identifier Label */}
            <div className="space-y-1.5">
              <label htmlFor="equipmentIdentifierLabel" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5">
                <Box size={12} className="text-primary" />
                <span>Equipment Identifier Label</span>
              </label>
              <input
                id="equipmentIdentifierLabel"
                type="text"
                maxLength={100}
                placeholder="e.g. VIN / Serial Number"
                className={inputClass('equipmentIdentifierLabel')}
                value={form.equipmentIdentifierLabel}
                onChange={e => setForm(f => ({ ...f, equipmentIdentifierLabel: e.target.value }))}
              />
              <p className="text-base-content/40 text-[10px] font-medium">
                Default: "Equipment Identifier"
              </p>
            </div>
          </div>
        </div>

        {/* Live Field Preview Card */}
        <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
          <div className="flex items-center gap-2 pb-3 border-b border-base-content/10">
            <Layers size={18} className="text-primary" />
            <h2 className="text-base font-black text-base-content uppercase tracking-wider">
              Field Preview
            </h2>
          </div>

          <div className="p-4 rounded-xl bg-base-200/50 border border-base-content/5 space-y-2">
            <p className="text-xs text-base-content/50 font-medium">
              When managing {SERVICE_REQUEST_LABEL.pluralLower} for <strong className="text-base-content font-bold">{form.name || 'this category'}</strong>, columns and form fields will appear as:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 pt-2">
              <div className="p-2.5 rounded-lg bg-base-100 border border-base-content/10 text-xs">
                <span className="text-[10px] font-black uppercase tracking-widest text-base-content/40 block">{SERVICE_REQUEST_LABEL.singular} Field</span>
                <span className="font-bold text-base-content">{form.requestNumberLabel || DEFAULT_REQUEST_NUMBER_LABEL}</span>
              </div>
              <div className="p-2.5 rounded-lg bg-base-100 border border-base-content/10 text-xs">
                <span className="text-[10px] font-black uppercase tracking-widest text-base-content/40 block">Customer Field</span>
                <span className="font-bold text-base-content">{form.customerNameLabel || 'Customer Name'}</span>
              </div>
              <div className="p-2.5 rounded-lg bg-base-100 border border-base-content/10 text-xs">
                <span className="text-[10px] font-black uppercase tracking-widest text-base-content/40 block">Asset Field</span>
                <span className="font-bold text-base-content">{form.assetIdentifierLabel || 'Asset Identifier'}</span>
              </div>
              <div className="p-2.5 rounded-lg bg-base-100 border border-base-content/10 text-xs">
                <span className="text-[10px] font-black uppercase tracking-widest text-base-content/40 block">Equipment Field</span>
                <span className="font-bold text-base-content">{form.equipmentIdentifierLabel || 'Equipment Identifier'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex flex-col-reverse sm:flex-row items-center gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate(TYPES_ROUTE)}
            className="w-full sm:w-auto px-6 py-3 border border-base-content/20 text-base-content/70 hover:bg-base-200 rounded-xl font-bold text-sm transition-all"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="w-full sm:flex-1 btn-premium justify-center"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-primary-content/30 border-t-primary-content rounded-full animate-spin"></div>
                <span>Saving Changes...</span>
              </>
            ) : (
              <>
                <Save size={16} />
                <span>Save Changes</span>
              </>
            )}
          </button>
        </div>
      </form>
      )}

      {/* Hierarchy Tree Tab */}
      {activeTab === 'hierarchy' && (
        <div className="space-y-6">
          {hierarchyLoading && hierarchyLevels.length === 0 ? (
            <div className="p-12 text-center space-y-3">
              <div className="w-8 h-8 border-3 border-primary/30 border-t-primary rounded-full animate-spin mx-auto"></div>
              <p className="text-xs text-base-content/50 font-medium">Loading hierarchy schema and tree nodes...</p>
            </div>
          ) : (
            <>
              {/* 1. Hierarchy Level Schema Builder */}
              <HierarchyLevelManager
                typeUid={id}
                initialLevels={hierarchyLevels}
                hasActiveOptions={hierarchyNodes.some((n) => n.isActive)}
                onLevelsSaved={(newLevels) => {
                  setHierarchyLevels(newLevels);
                  loadHierarchy();
                }}
              />

          {/* 2. Guided Cascading Option Adder */}
          <div id="hierarchy-node-adder">
            <HierarchyNodeAdder
              typeUid={id}
              levels={hierarchyLevels}
              nodes={hierarchyNodes}
              onNodeCreated={loadHierarchy}
              externalTargetDepth={quickAddTargetDepth}
              externalParentUid={quickAddParentUid}
            />
          </div>

          {/* 3. Interactive Visual Tree Explorer */}
          <HierarchyTreeView
            typeUid={id}
            levels={hierarchyLevels}
            tree={hierarchyTree}
            nodes={hierarchyNodes}
            onRefresh={loadHierarchy}
            onQuickAddChild={(node) => {
              setQuickAddTargetDepth(node.depth + 1);
              setQuickAddParentUid(node.uid);
              const adderEl = document.getElementById('hierarchy-node-adder');
              if (adderEl) {
                adderEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }}
            includeInactive={includeInactiveHierarchy}
            onToggleIncludeInactive={() => {
              const nextVal = !includeInactiveHierarchy;
              setIncludeInactiveHierarchy(nextVal);
              loadHierarchy(nextVal);
            }}
          />
          </>
          )}
        </div>
      )}
    </div>
  );
};

export default EditServiceRequestType;
